import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Chip,
  Alert,
  CircularProgress,
  Container,
  Divider,
  IconButton
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Security as SecurityIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import RoleForm from '../components/RoleForm';
import PermissionMatrix from '../components/PermissionMatrix';
import PermissionGuard from '../components/PermissionGuard';
import api from '../services/api';

const RoleDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [formOpen, setFormOpen] = useState(false);

  useEffect(() => {
    fetchRole();
  }, [id]);

  const fetchRole = async () => {
    try {
      const response = await api.get(`/api/roles/${id}`);
      setRole(response.data);
      setError('');
    } catch (error) {
      setError('Failed to load role details');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !role) {
    return (
      <Container maxWidth="lg">
        <Alert severity="error" sx={{ mt: 3 }}>
          {error || 'Role not found'}
        </Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/roles')}
          sx={{ mt: 2 }}
        >
          Back to Roles
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <IconButton onClick={() => navigate('/roles')} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>

          <Box sx={{ flexGrow: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <SecurityIcon sx={{ fontSize: 32, color: 'primary.main' }} />
              <Typography variant="h4">
                {role.name}
              </Typography>
              {role.is_system && (
                <Chip label="System Role" color="primary" />
              )}
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {role.description || 'No description'}
            </Typography>
          </Box>

          <PermissionGuard permission="users.edit">
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={() => setFormOpen(true)}
            >
              Edit Role
            </Button>
          </PermissionGuard>
        </Box>

        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Role Information
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Role Name
              </Typography>
              <Typography variant="body1">
                {role.name}
              </Typography>
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary">
                Total Permissions
              </Typography>
              <Typography variant="body1">
                {(role.permissions || []).length}
              </Typography>
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary">
                Created
              </Typography>
              <Typography variant="body1">
                {new Date(role.created_at).toLocaleDateString()}
              </Typography>
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary">
                Last Updated
              </Typography>
              <Typography variant="body1">
                {new Date(role.updated_at).toLocaleDateString()}
              </Typography>
            </Box>
          </Box>
        </Paper>

        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Permissions
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <PermissionMatrix
            permissions={role.permissions || []}
            onChange={() => {}}
            readOnly={true}
          />
        </Paper>
      </Box>

      <RoleForm
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSuccess={() => {
          fetchRole();
          setFormOpen(false);
        }}
        role={role}
      />
    </Container>
  );
};

export default RoleDetail;
